  // 앱/웹 공통: store에서 guestUserId 읽기
  // store에 없으면 query에서 읽기 (웹 다이렉트 접근 대응)
  // query.id 로 명시적으로 새 사주를 지정한 경우 query 가 우선
  const queryId = Number(route.query.id) || null;
  const newId = queryId || mobileSajuLnbStore.guestUserId || null;
  if (newId && (queryId || !mobileSajuLnbStore.guestUserId)) {
    mobileSajuLnbStore.setGuestUserId(newId);
  }<script setup>
// =============== 1. 초기 설정 ===============
// 외부 라이브러리
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';

// API
import { fetchBaseServiceAPI } from '@/api/baseServiceApi';

// Composables
import { useGesture } from '@/composables/calendar/useGesture';
import { useAnalysisServices } from '@/composables/saju-insight/useAnalysisServices';
import { useContentRate } from '@/composables/saju-insight/useContentRate';
import { useSajuData } from '@/composables/saju-insight/useSajuData';
import { useRnBridge } from '@/composables/useRnBridge';
import { isObjectEmpty, padNumber } from '@/composables/functions';

// Store
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useMobileSajuLnbStore } from '@/store/modules/mobileSajuLnb';

// Constants
import {
  MOBILE_YEARLY_FORTUNE_CONTENTS,
  MOBILE_YEARLY_FORTUNE_SECTION_PAGE_MAP,
} from '@/pages/mobile/analysis-saju-yearly-fortune/constants';
import { yearlyFortuneComponentMap } from '@/pages/mobile/analysis-saju-yearly-fortune/summaryComponentMap';

// Components
import YearlyFortuneCover from '@/pages/mobile/analysis-saju-yearly-fortune/_summary_01.vue';
import { addContentLogAPI } from '@/api/logServiceApi';
import YearlyFortuneTOC from '@/pages/mobile/analysis-saju-yearly-fortune/_summary_02.vue';
import PopDesc from '@/pages/mobile/analysis-saju-yearly-fortune/_pop_desc.vue';
import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import PopAnalysisInfoOther from '@/components/saju-insight/common/PopAnalysisInfoOther.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';
import SurveyCard from '@/components/calendar/drawer/pages/cards/SurveyCard.vue';
import AlertComponent from '@/components/common/Alert.vue';
import IconClose01 from '@/components/svg/IconClose01.vue';
import IconList from '@/components/svg/IconList.vue';

provide('fullWidthMode', true);

definePage({ meta: { layout: 'm-saju-insight' } });

const emit = defineEmits(['close']);

const route = useRoute();
const router = useRouter();
const analysisStore = useAnalysisStore();
const analysisDataStore = useAnalysisDataStore();
const paymentsStore = usePaymentsStore();
const mobileSajuLnbStore = useMobileSajuLnbStore();
const { serviceData, serviceContents, fetchContentByKey } = useAnalysisServices();

const { sajuData, yearlyFortuneData, yearlyFortuneSubData, baseCode, transdata } =
  storeToRefs(analysisDataStore);

// =============== 2. Alert / Dialog ===============
const { alertRef, dialogTitle, dialogText, dialogNextUrl, showAlert } = useDialogManager();
const warningDetected = ref(false);
const warningMessage = ref('');

const showWarningAlert = (message) => {
  warningDetected.value = true;
  warningMessage.value = message;
  showAlert({ title: message });
};

// =============== 3. 웹뷰 통신 ===============
/** 모바일 사용자 정보 */
const guestUserId = ref(null);
const isInitializing = ref(true);

const { debuggingHandler } = useRnBridge();

// =============== 4. PC 환경 ===============
const store = useStore();
const userId = ref(null);
userId.value = store.getters['user/id'];

// =============== 5. 사주 데이터 ===============
const { initWithValidation, isLoadingSajuData } = useSajuData();

const closeSajuInsight = () => {
  router.push('/saju-service');
};

const initializeSajuData = () =>
  initWithValidation(guestUserId.value, { showAlert, onRestricted: closeSajuInsight });

// =============== 6. 데이터 조회 ===============
// =============== 6-1. 이용중인 분석 정보 ===============

/** 권한에 따른 suggestionFlag: 권한 없음 = '1', 권한 있음 = '3' */
const suggestionFlag = computed(() => (serviceData.yearlyFortune.permission.value ? '3' : '1'));

/** 이용중인 신년운세 정보 요청 중 상태 */
const isLoadingAnalysisInfo = ref(false);

/** 이용중인 신년운세 정보 요청 완료 여부 */
const hasLoadedAnalysisInfo = ref(false);

/** 이용중인 신년운세 정보 요청 */
const getAnalysisInfo = async () => {
  isLoadingAnalysisInfo.value = true;
  hasLoadedAnalysisInfo.value = false;

  try {
    await fetchContentByKey('yearlyFortune');
    hasLoadedAnalysisInfo.value = true;
  } catch (e) {
    debuggingHandler({
      action: 'error',
      message: `신년운세 > 1. 이용중인 신년운세 정보 요청 실패: ${e}`,
    });
  } finally {
    isLoadingAnalysisInfo.value = false;
  }
};

// =============== 6-2. 분석 파라미터 ===============
/** 신년운세 데이터 요청 중 상태 */
const isFetchingYearlyFortune = ref(false);

/** 신년운세 데이터 요청 완료 여부 */
const hasFetchedYearlyFortune = ref(false);

/** 신년운세 데이터 요청 params */
const yearlyFortuneParams = computed(() => {
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return null;

  return {
    guestUserId: guestUserId.value,
    lang: 'KOR',
    userName: sajuData.value.inputdata.name ?? '',
    bln: sajuData.value.transdata.lucknum ?? null,
    gender: Number(sajuData.value.inputdata.gender) ?? null,
    unknownTime: sajuData.value.inputdata.unknown_time ?? null,
    analysisTypeId: 9,
    suggestionFlag: suggestionFlag.value,
    year: new Date().getFullYear(),
  };
});

// =============== 6-3. 기본 데이터 요청 ===============
// 신년운세는 6-2 상세 데이터 요청에서 필요한 데이터가 모두 포함되므로 별도 기본 데이터 요청 불필요

// =============== 6-4. 분석권 데이터 요청 ===============
/** 내 신년운세 데이터 요청 */
const fetchYearlyFortuneData = async () => {
  hasFetchedYearlyFortune.value = false;
  isFetchingYearlyFortune.value = true;
  try {
    if (isObjectEmpty(analysisDataStore.yearlyFortuneData)) {
      const result = await fetchBaseServiceAPI(yearlyFortuneParams.value);

      const data = result.data || result?.rag_data;
      if (data) {
        // 경고 응답 체크 (대운범위초과 등) - 데이터 저장하지 않고 경고 상태만 저장
        if (data['경고']) {
          warningDetected.value = true;
          warningMessage.value = data['경고']['메시지'] || '운세 분석이 어렵습니다.';
          analysisDataStore.setYearlyFortuneWarning(warningMessage.value);
          return;
        }
        analysisDataStore.setYearlyFortuneData(data);
        analysisDataStore.setYearlyFortuneSubData({});
        hasFetchedYearlyFortune.value = true;

        // 8_행운용어 → 용어해석 데이터 fetch
        const terms = data['8_행운용어'];
        if (Array.isArray(terms) && terms.length > 0) {
          await analysisDataStore.fetchSajuWords(terms.join(','));
        }
      }
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `신년운세 > 2-2. 분석권 데이터 요청 실패: ${e}` });
  } finally {
    isFetchingYearlyFortune.value = false;
  }
};

// =============== 6-5. 데이터 초기화 ===============
/** 신년운세 데이터 초기화 */
const initYearlyFortuneData = async () => {
  await fetchYearlyFortuneData();
};

// =============== 6-6. 페이지 데이터 새로고침 ===============
/** 신년운세 페이지 데이터 로딩 상태 */
const isRefreshingPageData = ref(false);

/** 신년운세 페이지 데이터 새로고침 */
const refreshYearlyFortunePageData = async () => {
  isRefreshingPageData.value = true;
  try {
    // 각 API를 개별적으로 처리하여 일부 실패해도 페이지가 표시되도록 함
    await Promise.allSettled([
      getAnalysisInfo().catch((e) => {
        console.warn('신년운세 > getAnalysisInfo 실패 (페이지 표시에는 영향 없음):', e);
      }),
      // 권한(canAccess) + 이용내역(만료일 등)을 access로 통합 조회 (타입별 /analysis/log 제거)
      // 액션 내부에서 실패를 흡수하므로 별도 catch 불필요
      analysisStore.fetchAnalysisAccess(guestUserId.value, [9]),
      paymentsStore.fetchTicketCount().catch((e) => {
        console.warn('신년운세 > fetchTicketCount 실패 (페이지 표시에는 영향 없음):', e);
      }),
      paymentsStore.getOwnPoint().catch((e) => {
        console.warn('신년운세 > getOwnPoint 실패 (페이지 표시에는 영향 없음):', e);
      }),
    ]);

    // 신년운세 데이터 초기화도 실패해도 페이지는 표시되도록 처리
    await initYearlyFortuneData().catch((e) => {
      console.warn('신년운세 > initYearlyFortuneData 실패 (페이지 표시에는 영향 없음):', e);
    });
  } catch (e) {
    // 예상치 못한 에러도 페이지 표시를 막지 않도록 처리
    console.warn('신년운세 > 페이지 새로고침 중 예상치 못한 에러 (페이지 표시에는 영향 없음):', e);
  } finally {
    // 항상 로딩 상태를 해제하여 페이지가 표시되도록 함
    isRefreshingPageData.value = false;
  }
};

// =============== 6-7. 컴포넌트별 데이터 슬라이스 ===============
const contents01Data = computed(() => ({
  사주정보: yearlyFortuneData.value?.['0_사주정보'],
  전체요약: yearlyFortuneData.value?.['0_전체요약'],
}));

const contents02Data = computed(() => ({
  사주정보: yearlyFortuneData.value?.['0_사주정보'],
  전체요약: yearlyFortuneData.value?.['0_전체요약'],
}));

const contents03Data = computed(() => ({
  전체요약: yearlyFortuneData.value?.['1_전체요약'],
  운세흐름: yearlyFortuneData.value?.['2_운세흐름'],
}));

const monthlyEnergyData = computed(() => ({
  전체요약: yearlyFortuneData.value?.['1_전체요약'],
}));

const contents04Data = computed(() => ({
  운세흐름: yearlyFortuneData.value?.['2_운세흐름'],
}));

const contents05Data = computed(() => ({
  운세해석: yearlyFortuneData.value?.['3_운세해석'],
}));

const contents06Data = computed(() => ({
  에너지방향: yearlyFortuneData.value?.['4_에너지방향'],
}));

const contents07Data = computed(() => ({
  에너지추천: yearlyFortuneData.value?.['5_에너지추천'],
  안정에너지: yearlyFortuneData.value?.['6_안정에너지'],
}));

const contents08Data = computed(() => ({
  종합전략: yearlyFortuneData.value?.['7_종합전략'],
}));

const guide02Data = computed(() => ({
  사주정보: yearlyFortuneData.value?.['0_사주정보'],
}));

// 페이지별 데이터 반환 함수
const getDataByPage = (page) => {
  // 페이지 → 컴포넌트 매핑: 3-4: Guide02, 5-6: Contents01, 7-8: Contents02, ...
  if (page >= 3 && page <= 4) return guide02Data.value;
  if (page >= 5 && page <= 6) return contents01Data.value;
  if (page >= 7 && page <= 8) return contents02Data.value;
  if (page >= 9 && page <= 10) return contents03Data.value;
  if (page >= 11 && page <= 12) return monthlyEnergyData.value;
  if (page >= 13 && page <= 14) return contents04Data.value;
  if (page >= 15 && page <= 16) return contents05Data.value;
  if (page >= 17 && page <= 18) return contents06Data.value;
  if (page >= 19 && page <= 20) return contents07Data.value;
  if (page >= 21 && page <= 22) return contents08Data.value;
  return null;
};

// =============== 7. 컨텐츠 평가 ===============
const { ratingResult, ratingStatus, getRate, postRate } = useContentRate(
  'YEARLY_FORTUNE',
  guestUserId,
);

// =============== 8. 페이지 상태 ===============
const currentPage = ref(0); // 현재 페이지

/** 동적으로 전체 페이지 수 계산 */
const totalPages = computed(() => {
  // 신년운세는 가이드 2개 + 컨텐츠 7개 = 9페이지
  return Object.keys(yearlyFortuneComponentMap).length + 1;
});

const showSummary02 = ref(false);

const showPopAnalysisInfo = ref(false);
const showPopAnalysisInfoOther = ref(false);

const goToCoverPage = () => {
  // 모바일은 별도 다시보기 버튼이 없어, 마지막 페이지에서 표지 복귀 시점을 다시보기로 집계
  addContentLogAPI('ayf_fortune_btn_restart');
  currentPage.value = 0;
  showSummary02.value = false;
};

// =============== 9. 팝업 / 모달 ===============
const showPopAnalysisInfoDo = async () => {
  if (isRefreshingPageData.value) {
    return;
  }

  // 신년운세권 사용중일 때,
  if (serviceData.yearlyFortune.permission.value) {
    showPopAnalysisInfo.value = false;
    showPopAnalysisInfoOther.value = false;
    return;
  }

  // 팝업을 열기 전에 포인트 정보를 다시 가져오기
  await paymentsStore.getOwnPoint();

  if (
    paymentsStore?.points < serviceContents.yearlyFortune.value?.pointPrice &&
    serviceData.yearlyFortune.ticketCount.value === 0
  ) {
    // 포인트가 적고, 분석권도 없을 때
    // 포인트 충전 팝업 표시 (분석권 구매 팝업 X)
    showPopAnalysisInfoOther.value = false;
    showPopAnalysisInfo.value = true;
  } else {
    // 포인트 또는 분석권이 충분할 때,
    // 분석권 구매 팝업 표시 (포인트 충전 팝업 X)
    showPopAnalysisInfoOther.value = true;
    showPopAnalysisInfo.value = false;
  }
};

// =============== 하단 탭 영역 (용어해석) ===============
const showPopDesc = ref(false);
const openPopDesc = () => {
  if (isRefreshingPageData.value) return;
  // 경고 상태면 결제 팝업 대신 경고 알림 표시
  const storeWarning = analysisDataStore.yearlyFortuneWarning;
  if (warningDetected.value || storeWarning) {
    showWarningAlert(warningMessage.value || storeWarning);
    return;
  }
  if (!serviceData.yearlyFortune.permission.value) {
    showPopAnalysisInfoDo();
    return;
  }
  showPopDesc.value = true;
};
const closePopDesc = () => {
  showPopDesc.value = false;
};

const handleClosePop = () => {
  showPopAnalysisInfo.value = false;
};

const handleChargePop = (shortageAmount) => {
  router.push({ path: '/purchase/points', query: { next: route.fullPath, shortageAmount } });
  showPopAnalysisInfo.value = false;
};

const handleClosePopOther = () => {
  showPopAnalysisInfoOther.value = false;
};

const handleChargePopOtherProduct = () => {
  router.push('/purchase/tickets');
  showPopAnalysisInfoOther.value = false;
};

/** 신년운세 결제 성공 후 실행 */
const paymentSuccessDo = async (success) => {
  if (success) {
    try {
      // 로딩 상태 먼저 활성화
      isRefreshingPageData.value = true;

      // 기존 데이터 클리어 (새 권한으로 새 데이터 요청을 위해)
      analysisDataStore.setYearlyFortuneData({});

      // 인덱스 초기화
      analysisStore.setYearlyFortuneIndex(1);
      analysisStore.setSelectedContentIndex(null);
      currentPage.value = 0;

      await refreshYearlyFortunePageData();

      // 평가 데이터는 실패해도 페이지 표시에 영향 없도록 별도 처리
      getRate().catch(() => {
        // 에러는 이미 함수 내부에서 처리됨
      });
      mobileSajuLnbStore.setNeedRefresh(true);
    } catch (e) {
      debuggingHandler({ action: 'error', message: `신년운세권 결제 후 새로고침 중 오류: ${e}` });
    }
  }
};

// =============== 10. 페이지 네비게이션 ===============
/** 페이지 이전/다음 이동 로그 — 버튼/스와이프 공통, 이동이 실제 발생한 경우만 발화 */
const logPageMove = () => {
  addContentLogAPI({
    contentType: 'ayf_fortune_page_opt3',
    option3: String(currentPage.value),
  });
};

const clickPrevPage = () => {
  if (currentPage.value < 1) return;
  else currentPage.value -= 1;
  logPageMove();
  window.scrollTo(0, 0);
};

const clickNextPage = () => {
  // 3페이지부터 데이터 필요 → 경고 상태면 진입 차단
  if (currentPage.value + 1 >= 3 && warningDetected.value) {
    showWarningAlert(warningMessage.value);
    return;
  }
  // 1~8페이지(Guide01~Contents02)는 무료, 9페이지(QuarterEnergy)부터 권한 필요
  if (
    currentPage.value === 8 &&
    currentPage.value + 1 >= 9 &&
    !serviceData.yearlyFortune.permission.value
  ) {
    showPopAnalysisInfoDo();
    return;
  }
  if (currentPage.value >= totalPages.value) {
    goToCoverPage();
  } else {
    currentPage.value += 1;
    logPageMove();
  }
  window.scrollTo(0, 0);
};

// 키보드 방향키로 페이지 이동 (개발용)
const onKeydown = (e) => {
  if (e.key === 'ArrowLeft') clickPrevPage();
  else if (e.key === 'ArrowRight') clickNextPage();
};
onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));

/** 통합 제스처 핸들러 */
const { handleStart, handleMove, handleEnd } = useGesture({
  onSwipeLeft: clickNextPage, // 왼쪽 스와이프 → 다음 페이지 이동
  onSwipeRight: clickPrevPage, // 오른쪽 스와이프 → 이전 페이지 이동
  SWIPE_THRESHOLD: 150,
});

const handleNavigateToPage = async (id) => {
  // id를 사용하여 MOBILE_YEARLY_FORTUNE_SECTION_PAGE_MAP에서 페이지 매핑
  const mappedPage = MOBILE_YEARLY_FORTUNE_SECTION_PAGE_MAP[id];

  if (mappedPage === undefined) {
    console.error('매핑되지 않은 목차 ID:', id);
    debuggingHandler({
      action: 'error',
      message: `신년운세 > 매핑되지 않은 목차 ID: ${id}`,
    });
    return;
  }

  // 목차 클릭 로그(무료/유료). PC와 동일 content_type 재사용
  addContentLogAPI({
    contentType: serviceData.yearlyFortune.permission.value
      ? 'ayf_fortune_toc_paid_opt3'
      : 'ayf_fortune_toc_free_opt3',
    option3: String(id),
  });

  // 3페이지부터 데이터 필요 → 경고 상태면 진입 차단 (store도 함께 확인)
  const storeWarning = analysisDataStore.yearlyFortuneWarning;
  if (mappedPage >= 3 && (warningDetected.value || storeWarning)) {
    showWarningAlert(warningMessage.value || storeWarning);
    return;
  }
  // 데이터가 아직 없으면 fetch하여 경고 여부 먼저 확인
  if (
    mappedPage >= 3 &&
    isObjectEmpty(analysisDataStore.yearlyFortuneData) &&
    !warningDetected.value
  ) {
    await fetchYearlyFortuneData();
    if (warningDetected.value || analysisDataStore.yearlyFortuneWarning) {
      showWarningAlert(warningMessage.value || analysisDataStore.yearlyFortuneWarning);
      return;
    }
  }
  // 1~8페이지(Guide01~Contents02)는 무료, 9페이지(QuarterEnergy)부터 권한 필요
  if (mappedPage >= 9 && !serviceData.yearlyFortune.permission.value) {
    showPopAnalysisInfoDo();
    return;
  }

  currentPage.value = mappedPage;
};

// =============== 11. 목차 UI ===============
const openFlag = ref('');
const openContentsList = () => {
  if (openFlag.value === '') openFlag.value = 'open';
  else openFlag.value = '';
};
const closeContentsList = () => (openFlag.value = '');

// 페이지별 컴포넌트 반환 함수
const getComponentByPage = (page) => {
  return yearlyFortuneComponentMap[page] || null;
};

// 페이지별 CSS 클래스 반환 함수
const getComponentClass = (page) => {
  return page % 2 === 1 ? 'mobile-content-first' : 'mobile-content-second';
};

/** index값으로 페이지 이동 */
const updateIndexNo = (id, fromTocClick = false) => {
  // id를 사용하여 MOBILE_YEARLY_FORTUNE_SECTION_PAGE_MAP에서 페이지 매핑
  const mappedPage = MOBILE_YEARLY_FORTUNE_SECTION_PAGE_MAP[id];

  if (mappedPage === undefined) {
    console.warn(`매핑되지 않은 목차 ID: ${id}`);
    return;
  }

  // 플로팅 목차 리스트 클릭 시에만 발화 — 프로그래밍 방식 이동(스토어 연동)은 발화하지 않음
  if (fromTocClick) {
    addContentLogAPI({
      contentType: serviceData.yearlyFortune.permission.value
        ? 'ayf_fortune_toc_paid_opt3'
        : 'ayf_fortune_toc_free_opt3',
      option3: String(id),
    });
  }

  // 3페이지부터 데이터 필요 → 경고 상태면 진입 차단
  if (mappedPage >= 3 && warningDetected.value) {
    showWarningAlert(warningMessage.value);
    return;
  }

  // 1~8페이지(Guide01~Contents02)는 무료, 9페이지(QuarterEnergy)부터 권한 필요
  if (mappedPage >= 9 && !serviceData.yearlyFortune.permission.value) {
    showPopAnalysisInfoDo();
    return;
  }

  // 동적 페이지 수 범위 체크
  if (mappedPage > totalPages.value) {
    console.warn(`페이지 번호 ${mappedPage}가 전체 페이지 수 ${totalPages.value}를 초과합니다.`);
    return;
  }

  currentPage.value = mappedPage;
  closeContentsList();
};

// =============== 12. 공통 액션 ===============
/** 팝업 닫기 */
const close = () => {
  router.push(`/m/saju-insight/${guestUserId.value}`);
};

const resetPopup = () => {
  currentPage.value = 0;
  showSummary02.value = false;
  showPopDesc.value = false;
};

const onClickRecharge = (shortageAmount) => {
  router.push({
    path: '/purchase/points',
    query: { next: route.fullPath, shortageAmount },
  });
};

const handelShowSummary02Open = async () => {
  // 시작하기 버튼 클릭 시 데이터 prefetch하여 경고 여부 먼저 확인
  const hasWarning = await prefetchAndCheckWarning();
  if (hasWarning) {
    showWarningAlert(analysisDataStore.yearlyFortuneWarning);
    return;
  }
  // 조회 로그(무료/유료) — 실제 진입(경고 없음) 시점. PC와 동일 content_type 재사용(디바이스 구분은 백엔드 option2).
  addContentLogAPI(
    serviceData.yearlyFortune.permission.value
      ? 'ayf_fortune_btn_start_paid'
      : 'ayf_fortune_btn_start_free',
  );
  showSummary02.value = true;
  window.scrollTo(0, 0);
};

/** 신년운세 데이터 미리 fetch하여 경고 여부 확인 */
const prefetchAndCheckWarning = async () => {
  if (analysisDataStore.yearlyFortuneWarning) return true;
  if (!isObjectEmpty(analysisDataStore.yearlyFortuneData)) return false;
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return false;

  isFetchingYearlyFortune.value = true;
  try {
    const result = await fetchBaseServiceAPI(yearlyFortuneParams.value);
    const data = result.data || result?.rag_data;
    if (data?.['경고']) {
      const message = data['경고']['메시지'] || '운세 분석이 어렵습니다.';
      warningDetected.value = true;
      warningMessage.value = message;
      analysisDataStore.setYearlyFortuneWarning(message);
      return true;
    }
    if (data) {
      analysisDataStore.setYearlyFortuneData(data);
      analysisDataStore.setYearlyFortuneSubData({});
    }
    return false;
  } catch (e) {
    debuggingHandler({ action: 'error', message: `모바일 신년운세 prefetch 실패: ${e}` });
    return false;
  } finally {
    isFetchingYearlyFortune.value = false;
  }
};

// =============== 13. Watch ===============
watch(
  () => mobileSajuLnbStore.guestUserId,
  (newId) => {
    if (newId) guestUserId.value = newId;
  },
);

watch(guestUserId, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    const result = await initializeSajuData();
    if (result?.failed || result?.unknownTime || result?.futureSaju) return;
    // 사주 ID 변경 시 이전 사주의 분석 데이터 초기화 (base-service 재호출 보장)
    analysisDataStore.setYearlyFortuneData({});
    analysisDataStore.setYearlyFortuneSubData({});
    await refreshYearlyFortunePageData();
    // 평가 데이터는 실패해도 페이지 표시에 영향 없도록 별도 처리
    getRate().catch(() => {
      // 에러는 이미 함수 내부에서 처리됨
    });
  }
});

// 인덱스
// const indexNo = ref(1)
watch(currentPage, (newPage) => {
  analysisStore.setYearlyFortuneIndex(newPage);
  // currentPage.value = newIndex
});

watch(
  () => analysisStore.selectedContentIndex,
  (newIdx) => {
    // newIdx는 contentIdx (0, 1, 2, ...)
    // YEARLY_FORTUNE_CONTENTS에서 contentIdx에 해당하는 항목 찾기
    // contentIdx 0 → id 1, contentIdx 1 → id 2, contentIdx 2 → id 3, ...
    if (newIdx !== null && MOBILE_YEARLY_FORTUNE_CONTENTS[newIdx]) {
      const target = MOBILE_YEARLY_FORTUNE_CONTENTS[newIdx];
      if (target?.id) updateIndexNo(target.id);
    }
  },
  { immediate: true },
);

// =============== 14. 라이프사이클 ===============
onMounted(async () => {
  // 웹 환경: 비로그인 접근 차단
  if (!userId.value && !sessionStorage.getItem('X-Token')) {
    showAlert({ title: '로그인 후 이용 가능합니다.', nextUrl: () => router.push('/auth/login') });
    return;
  }

  // 앱/웹 공통: store에서 guestUserId 읽기
  // store에 없으면 query에서 읽기 (웹 다이렉트 접근 대응)
  // query.id 로 명시적으로 새 사주를 지정한 경우 query 가 우선
  const queryId = Number(route.query.id) || null;
  const newId = queryId || mobileSajuLnbStore.guestUserId || null;
  if (newId && (queryId || !mobileSajuLnbStore.guestUserId)) {
    mobileSajuLnbStore.setGuestUserId(newId);
  }

  isInitializing.value = false;

  // 같은 ID여도 강제 재조회 (브라우저 캐시 대응)
  // → watch가 트리거되어 데이터 재조회
  if (newId) {
    guestUserId.value = null;
    await nextTick();
    guestUserId.value = newId;
  }
});

onUnmounted(() => {
  analysisStore.setYearlyFortuneIndex(1);
  analysisStore.setSelectedContentIndex(null);
});
</script>

<template>
  <!-- 분석권 및 포인트 부족 -->
  <PopAnalysisInfo
    :isVisible="showPopAnalysisInfo"
    :analysisType="serviceContents.yearlyFortune.value"
    @close="handleClosePop"
    @charge="handleChargePop"
  />
  <!-- 분석권 또는 포인트 사용 가능 -->
  <PopAnalysisInfoOther
    :isVisible="showPopAnalysisInfoOther"
    :analysisType="serviceContents.yearlyFortune.value"
    :ticketCountData="serviceData.yearlyFortune.ticketData.value"
    :ticketCount="serviceData.yearlyFortune.ticketCount.value"
    @close="handleClosePopOther"
    @charge="handleChargePopOtherProduct"
    @payment-success="paymentSuccessDo"
  />

  <div
    class="pop-saju-reading-container mobile st-analysis-yearly-fortune"
    @mousedown="handleStart"
    @mousemove="handleMove"
    @mouseup="handleEnd"
    @touchstart="handleStart"
    @touchmove="handleMove"
    @touchend="handleEnd"
  >
    <AnalysisMobileHeader title="신년운세" :guestUserId="guestUserId" @close="close" />
    <div v-if="currentPage === 0" class="pop-ana-saju-reading-inner intro">
      <YearlyFortuneCover
        v-if="currentPage === 0 && !showSummary02"
        :guestUserId="guestUserId"
        :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
        :ticketCount="paymentsStore?.analysisTicketCount || 0"
        :getAnalysisLogDataList="serviceData.yearlyFortune.logData.value"
        :hasPermission="serviceData.yearlyFortune.permission.value"
        :ticketCountData="serviceData.yearlyFortune.ticketData.value"
        :yearlyFortuneLogData="serviceData.yearlyFortune.logData.value"
        @handleNavigateToPage="handleNavigateToPage"
        @handelShowSummary02Open="handelShowSummary02Open"
        @recharge="onClickRecharge"
      />
      <AnalysisLoadingOverlay
        :loading="
          currentPage === 0 &&
          showSummary02 &&
          (isInitializing || isLoadingSajuData || isFetchingYearlyFortune)
        "
        :message="'신년운세 분석중입니다!'"
      />
      <YearlyFortuneTOC
        v-if="currentPage === 0 && showSummary02"
        :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
        :ticketCount="paymentsStore?.analysisTicketCount || 0"
        :hasPermission="serviceData.yearlyFortune.permission.value"
        :initializing="isInitializing || isLoadingSajuData"
        @navigateToPage="handleNavigateToPage"
        @updateShowSummary02="(value) => (showSummary02 = value)"
        @goPopAnalysis="showPopAnalysisInfoDo"
        @recharge="onClickRecharge"
      />
    </div>

    <div v-if="currentPage !== 0" class="pop-ana-saju-reading-inner st-page st-content-page">
      <div v-if="currentPage >= 1 && currentPage <= totalPages" class="pop-body mobile">
        <component
          :is="getComponentByPage(currentPage)"
          :class="getComponentClass(currentPage)"
          :isMobile="true"
          :data="getDataByPage(currentPage)"
          :userName="mobileSajuLnbStore?.userName"
        />
      </div>

      <div v-if="currentPage === totalPages" class="pop-body mobile rating">
        <div class="sr__no-content-page">
          <SurveyCard
            style="width: 100%"
            :ratingResult="ratingResult"
            :ratingStatus="ratingStatus"
            @postRate="postRate"
          />
        </div>
      </div>

      <div class="contentsListBtnBox mobile">
        <div class="list-box" :class="openFlag">
          <ul class="list-li-box">
            <li
              class="li mobile"
              :class="{ 'li--locked': !serviceData.yearlyFortune.permission.value }"
              v-for="item in MOBILE_YEARLY_FORTUNE_CONTENTS.filter(
                (content) => content.title !== '',
              )"
              :key="item.id"
              @click="serviceData.yearlyFortune.permission.value && updateIndexNo(item.id, true)"
            >
              {{ item.title }}
            </li>
          </ul>
        </div>
      </div>
      <div class="pop-footer-page-box">
        <div class="pop-desc-box">
          <button class="btn-desc" @click="openPopDesc">📖 용어해석</button>
        </div>
        <div class="pop-pagination">
          <IconList
            @click="openContentsList('open')"
            v-if="openFlag === ''"
            style="margin-top: 3px"
          />
          <IconClose01
            @click="openContentsList('')"
            v-if="openFlag === 'open'"
            style="margin-top: 3px"
          />
          <button class="pop-pagination__prev" @click="clickPrevPage">
            <i class="icon-chevron"></i>
          </button>
          <div class="pop-pagination__index">{{ padNumber(currentPage) }} / {{ totalPages }}</div>
          <button class="pop-pagination__next" @click="clickNextPage">
            <i class="icon-chevron right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 용어해석 팝업 -->
  <PopDesc v-model="showPopDesc" :guestUserId="guestUserId" @close="closePopDesc" />

  <!-- 경고 팝업 (대운범위초과 등) -->
  <AlertComponent ref="alertRef" :title="dialogTitle" :text="dialogText" :nextUrl="dialogNextUrl" />
</template>

<style lang="scss">
@import '@/assets/nangman_style/popup/saju-reading_mobile.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common-page.scss';
@import '@/assets/nangman_style/_analysis_main_color.scss';

/* 네이버 인앱브라우저: 콘텐츠가 짧으면 document 스크롤 불가 → 주소창이 페이지 위에 오버레이됨.
   강제로 viewport보다 약간 더 크게 만들어 항상 스크롤 가능 상태 유지 */
.st-analysis-yearly-fortune .detailed-overview,
.st-analysis-yearly-fortune .pop-ana-saju-reading-inner.st-page {
  min-height: calc(100vh + 1px);
}

/* 네이버 인앱브라우저 주소창 오버레이 방지: 문서 레벨 스크롤 전환 */
.pop-saju-reading-container.mobile.st-analysis-yearly-fortune {
  height: auto;
  min-height: 100vh;
  overflow: visible;

  > .header {
    position: sticky;
    top: 0;
    z-index: 20;
  }
}

.pop-ana-saju-reading-inner {
  height: auto;
  min-height: calc(100vh - 52px - var(--safe-area-top, 0px));
  overflow: visible;
  &.intro {
    overflow: visible;
    padding-bottom: var(--safe-area-bottom, 0px);
  }
  &.st-page {
    height: auto;
    min-height: calc(100vh - 52px - var(--safe-area-top, 0px));
    overflow: visible;
    padding-bottom: calc(52px + var(--safe-area-bottom, 0px));
  }
  .btn-footer-box {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    .btn-white {
      display: flex;
      width: 100%;
      height: 48px;
      padding: 14px 20px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 6px;
      border: 1px solid var(--Gray-300, #ebebeb);
      background: var(--gray-100-white, #fff);
      box-shadow: 0px -10px 10px 0px rgba(0, 0, 0, 0.05);
      // color: var(--Gray-800, #333);
      color: $analysis-personality;
      text-align: center;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
    }
  }
  .pop-body {
    height: auto;
    padding: 0px;
    flex: 1;
    overflow: visible;
    .v-row {
      margin: 0px !important;
    }
    .personality-wrapper {
      padding: 24px 16px;
    }
    &.rating {
      height: 100%;
    }
  }
}
.st-analysis-yearly-fortune {
  .pop-ana-saju-reading-inner,
  .overview-cover {
    /* PC 신년운세 목차 배경색과 동일 */
    background: #fdf9f3;
  }
  /* 신년운세 모바일 첫화면 상단 이름 텍스트 스타일 */
  .overview-cover__top {
    color: #4a3b32;
    font-family: Pretendard;
    font-size: 32px;
    font-style: normal;
    font-weight: 400;
    line-height: 44px; /* 137.5% */
  }
  /* 신년운세 이용하기 버튼 스타일 */
  .btn-footer-box {
    .btn-white {
      color: #be3331 !important; /* 신년운세 컬러 */
      border-color: #be3331 !important;
    }
  }
}
.st-analysis-yearly-fortune .pop-ana-saju-reading-inner .pop-body {
  background: #fff;
}

.st-analysis-yearly-fortune .pop-ana-saju-reading-inner.st-content-page {
  background-color: #fff !important;
  padding-bottom: calc(52px + var(--safe-area-bottom, 0px));
  overflow-x: hidden !important;

  .contentsListBtnBox.mobile {
    position: fixed;
    bottom: calc(52px + var(--safe-area-bottom, 0px));
    right: 0;
  }
}

/* 모바일에서 좌우 컬럼 분리 */
.mobile-content-first {
  .mobile-second {
    display: none !important;
  }
  .mobile-first {
    display: block !important;
  }
}

.mobile-content-second {
  .mobile-first {
    display: none !important;
  }
  .mobile-second {
    display: block !important;
  }
}

/* 용어해석 버튼 및 목차 스타일 - 50%씩 반반, 높이 동일 */
.st-analysis-yearly-fortune {
  .pop-footer-page-box {
    .pop-desc-box {
      width: 50% !important;
      height: 52px;
      .btn-desc {
        border-radius: 0;
      }
    }
    .pop-pagination {
      width: 50% !important;
      flex: none !important;
      height: 52px;
    }
  }
}
</style>
